import { jsxs, Fragment, jsx } from '@emotion/react/jsx-runtime';
import { FsnGmLogo } from '@fusion/components/GmLogo';
import MenuIcon from '@mui/icons-material/Menu';
import AppBar from '@mui/material/AppBar';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import { useTheme, styled } from '@mui/material/styles';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';

const FSN_HEADER_HEIGHT = '64px';
const classPrefix = 'fsn-header';
const classes = {
    root: `${classPrefix}`,
    xs: `${classPrefix}-xs`,
    menuButton: `${classPrefix}-menu-button`,
    logoContainer: `${classPrefix}-logo-container`,
    logoLink: `${classPrefix}-logo-link`,
    title: `${classPrefix}-title`,
    titleLink: `${classPrefix}-title-link`,
    headerActions: `${classPrefix}-header-actions`,
};
const testIds = classes;
const logoSize = {
    default: 40,
    xs: 30,
};
/**
 * This function creates the styled root (AppBar) component along with all
 * child style definitions.
 * @param hasNavigation indicating if it has children to be placed below header
 */
const createStyledAppBar = (hasNavigation) => styled(AppBar)(({ theme }) => {
    let color;
    let menuColor;
    let backgroundColor;
    if (theme.palette.mode === 'dark') {
        color = theme.palette.common.white;
        menuColor = theme.palette.common.white;
    }
    else {
        color = theme.palette.common.black;
        backgroundColor = theme.palette.background.paper;
        menuColor = theme.palette.primary.main;
    }
    return {
        [`&.${classes.root}`]: {
            display: 'flex',
            color,
            height: FSN_HEADER_HEIGHT,
            backgroundColor: backgroundColor,
            zIndex: 1050,
            ...(theme.palette.mode === 'dark' &&
                hasNavigation && {
                borderBottom: `5px solid ${theme.palette.divider}`,
            }),
            ...(hasNavigation && {
                boxShadow: 'none',
            }),
        },
        [`& .${classes.menuButton}`]: {
            marginLeft: '-12px',
            marginRight: '12px',
            color: menuColor,
        },
        [`& .${classes.logoContainer}`]: {
            display: 'flex',
            height: `${logoSize.default}px`,
        },
        [`&.${classes.xs} .${classes.logoContainer}`]: {
            height: `${logoSize.xs}px`,
        },
        [`& .${classes.title}`]: {
            marginLeft: '10px',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            color: color,
        },
        [`& .${classes.headerActions}`]: {
            display: 'flex',
            marginLeft: 'auto',
            paddingLeft: '16px',
        },
    };
});
/**
 * The header component provides the primary top-frame of the application.
 * It is where the application name resides, as well as providing a way for user to collapse the sidenav.
 * It is also a good place to put global actions (see content guidelines, below).
 */
const FsnHeader = (props) => {
    // Extract props and set defaults
    const { position = 'relative', title, menuVisible = true, menuOnClick, customCssClass, homeRoute = '/', navigation, children, } = props;
    const theme = useTheme();
    const isGreaterThanXSmall = useMediaQuery(theme.breakpoints.up('sm'));
    const logoWidth = isGreaterThanXSmall ? logoSize.default : logoSize.xs;
    const hasNavigation = !!navigation;
    const HeaderAppBar = createStyledAppBar(hasNavigation);
    const getRootClassName = () => {
        let className = classes.root;
        if (!isGreaterThanXSmall) {
            className = `${className} ${classes.xs}`;
        }
        if (customCssClass) {
            className = `${className} ${customCssClass}`;
        }
        return className;
    };
    return (jsxs(Fragment, { children: [jsx(HeaderAppBar, { elevation: 3, "data-testid": testIds.root, position: position, className: getRootClassName(), children: jsxs(Toolbar, { children: [menuVisible && (jsx(IconButton, { "data-testid": testIds.menuButton, className: classes.menuButton, color: "inherit", "aria-label": "toggle menu", onClick: menuOnClick, size: "large", children: jsx(MenuIcon, {}) })), jsx("div", { className: classes.logoContainer, "data-testid": testIds.logoContainer, children: jsx(Link, { "data-testid": testIds.logoLink, href: homeRoute, underline: "none", "aria-label": homeRoute ? 'App Logo navigates back to home path' : 'app title', children: jsx(FsnGmLogo, { width: logoWidth }) }) }), jsx(Typography, { "data-testid": homeRoute ? classes.title : classes.title, className: classes.title, variant: "appTitle", children: jsx(Link, { "data-testid": testIds.titleLink, color: "inherit", href: homeRoute, underline: "none", "aria-label": homeRoute ? 'App Title navigates back to home path' : 'App Title', children: title }) }), children && (jsx("div", { className: classes.headerActions, "data-testid": testIds.headerActions, children: children }))] }) }), position === 'fixed' && jsx(Toolbar, {}), jsx(Fragment, { children: navigation })] }));
};

export { FSN_HEADER_HEIGHT, FsnHeader, classes, testIds };
